import React from 'react';
import {
  Folder, FolderOpen, Inbox, MoreVertical, Pencil, Trash2, LayoutGrid,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Row = ({ item, active, onSelect, actions, testId }) => {
  const Icon = item.icon;
  return (
    <div
      className={`group flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer transition-colors ${
        active
          ? 'bg-[#F9F8F4] text-[#14213D] border border-[#D4AF37]/40'
          : 'text-[#5A6577] hover:text-[#14213D] hover:bg-[#F9F8F4] border border-transparent'
      }`}
      onClick={onSelect}
      data-testid={testId}
    >
      <Icon
        className="h-4 w-4 flex-shrink-0"
        strokeWidth={1.75}
        style={item.color ? { color: item.color } : undefined}
      />
      <span className="text-sm font-medium truncate flex-1 min-w-0">{item.label}</span>
      {typeof item.count === 'number' && (
        <span className={`text-xs tabular-nums flex-shrink-0 ${active ? 'text-[#14213D]' : 'text-[#5A6577]/70'}`}>
          {item.count}
        </span>
      )}
      {actions}
    </div>
  );
};

export default function FolderRail({ folders, uncategorizedCount, active, onSelect, onEdit, onDelete }) {
  return (
    <aside className="bg-white border border-[#EBE7E0] rounded-lg p-3 h-fit sticky top-20" data-testid="folder-rail">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577]/70 font-semibold px-2 mb-2">Folders</p>

      <div className="space-y-1">
        <Row
          item={{ label: 'All lessons', icon: LayoutGrid }}
          active={active === 'all'}
          onSelect={() => onSelect('all')}
          testId="folder-all"
        />
        <Row
          item={{ label: 'Uncategorized', icon: Inbox, count: uncategorizedCount }}
          active={active === 'root'}
          onSelect={() => onSelect('root')}
          testId="folder-uncategorized"
        />

        {folders.length > 0 && (
          <div className="h-px bg-[#EBE7E0] my-2" />
        )}

        {folders.map((f) => (
          <Row
            key={f.id}
            item={{
              label: f.name,
              icon: active === f.id ? FolderOpen : Folder,
              color: f.color,
              count: f.lesson_count || 0,
            }}
            active={active === f.id}
            onSelect={() => onSelect(f.id)}
            testId={`folder-${f.id}`}
            actions={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 -mr-1 rounded text-[#5A6577] hover:text-[#14213D] hover:bg-white flex-shrink-0"
                    data-testid={`folder-menu-${f.id}`}
                    aria-label={`Actions for ${f.name}`}
                  >
                    <MoreVertical className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(f); }} data-testid={`folder-edit-${f.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onDelete(f); }}
                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                    data-testid={`folder-delete-${f.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        ))}
      </div>
    </aside>
  );
}
